import './EmailSidePanel.scss';
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useFlowBuilderContext } from 'pages/FlowBuilder/context/flow-builder.context';
import { TSlidingSidePanelProps } from 'components/ExperienceCanvas/types';
import SlidingSidePanel from 'components/ExperienceCanvas/SlidingSidePanel/SlidingSidePanel';
import IconEmail from 'pages/FlowBuilder/assets/icons/IconEmail';
import AttributesMappingTable, { AttributeErrorProps } from '../shared/AttributesMappingTable/AttributesMappingTable';
import EmailTemplatePreview from './EmailTemplatePreview/EmailTemplatePreview';
import TemplateSelector from '../shared/TemplateSelector/TemplateSelector';
import {
  TEmailActionPayload,
  TEmailPreviewData,
  TEmailProviderTemplate,
  TEmailProviderTemplatePayload,
  TemplatePayloadData,
} from 'components/FlowBuilder/FlowSidePanels/types';
import useSettings from 'hooks/useSetting';
import { useThunkDispatch as useDispatch } from 'hooks/reduxHooks';
import { updateEmailTemplateData } from 'store/email/email.actions';
import useEmailTemplates from 'hooks/useEmailTemplates';
import useEmailTemplate from 'hooks/useEmailTemplate';
import useConfirmNavigationModal from 'hooks/useConfirmNavigationModal';
import usePlugins, { TContextPlugin } from 'hooks/useContextPlugins';
import IconDiscardChanges from 'components/Shared/Icons/IconDiscardChanges';
import { isEqual } from 'lodash';
import { capitalizeFirstCharacter } from 'helpers/common.helper';
import EmailSMSUnavailable from 'components/FlowBuilder/FlowSidePanels/shared/EmailSMSUnavailable/EmailSMSUnavailable';
import { deriveTemplatePayloadData, serializeTemplatePayloadData } from '../helpers';
import { getFormattedContextAttributeDisplay } from 'pages/FlowBuilder/helpers/flow-builder.helpers';

const MAIN_CLASS = 'email-side-panel';
const CLASSES = {
  CONTAINER: `${MAIN_CLASS}__container`,
  TEMPLATE_AREA: `${MAIN_CLASS}__container__template-area`,
  PREVIEW_AREA: `${MAIN_CLASS}__container__preview-area`,
};

function EmailSidePanel() {
  const dispatch = useDispatch();
  const { previewTextLanguage: selectedLanguageCode } = useSettings();
  const { emailTemplates, emailTemplatesError } = useEmailTemplates();
  const providerTemplateOptions = useMemo(
    () =>
      emailTemplates.map((template) => ({
        key: template.id,
        name: template.name,
      })),
    [emailTemplates],
  );

  const { plugins }: { plugins: TContextPlugin[] } = usePlugins();

  const mappedAttributes = useMemo(() => {
    if (!plugins || plugins.length === 0) return [];
    const sorted = [...plugins]
      .sort((a, b) => a.id.localeCompare(b.id))
      .map((plugin) =>
        Object.entries(plugin.values).map(([attributeName, attribute]) => ({
          name: `${plugin.name} / ${attributeName}`,
          subText: [capitalizeFirstCharacter(attribute.valueType), `{{${attribute.id}}}`],
          id: attribute.uid,
          attribute,
        })),
      )
      .flat();

    return sorted;
  }, [plugins]);

  const [selectedProviderTemplate, setSelectedProviderTemplate] = useState<TEmailProviderTemplate | null>(null);
  const [currentTemplatePayloadData, setCurrentTemplatePayloadData] = useState<TemplatePayloadData | null>(null);

  const { emailTemplate, emailTemplateError, isEmailTemplateFetching, refetchEmailTemplate } = useEmailTemplate(
    selectedProviderTemplate?.key ?? '',
  );
  const {
    sidePanels: { email: emailSidePanel },
  } = useFlowBuilderContext();
  const { isOpen, isViewOnly, email: emailState } = emailSidePanel;

  const emailActionPayload = useMemo<TEmailActionPayload | null>(() => {
    if (!emailState?.payload) {
      return null;
    }
    const {
      payload: { template },
    } = emailState;
    return template?.[selectedLanguageCode] || null;
  }, [emailState, selectedLanguageCode]);

  const selectedProviderTemplatePayload = useMemo<TEmailProviderTemplatePayload | null>(() => {
    if (!selectedProviderTemplate || !emailTemplate?.hasActiveVersion) {
      return null;
    }

    return emailTemplate;
  }, [selectedProviderTemplate, emailTemplate]);

  const defaultTemplatePayloadData = useMemo<TemplatePayloadData | null>(() => {
    if (
      !emailActionPayload ||
      !selectedProviderTemplatePayload ||
      emailActionPayload.providerTemplateId !== selectedProviderTemplatePayload.id
    ) {
      return null;
    }
    const templatePayloadData = deriveTemplatePayloadData(
      selectedProviderTemplatePayload.placeholderKeys || [],
      emailActionPayload.providerTemplateData || {},
      mappedAttributes,
    );

    return templatePayloadData;
  }, [emailActionPayload, selectedProviderTemplatePayload, mappedAttributes]);

  const payloadDataErrors = useMemo(() => {
    if (!currentTemplatePayloadData && !defaultTemplatePayloadData) {
      return undefined;
    }
    const errors: Record<string, AttributeErrorProps> = {};

    Object.entries({ ...(defaultTemplatePayloadData ?? {}), ...currentTemplatePayloadData }).forEach(([key, value]) => {
      const attributeId = value.attribute.match(/{{(.*?)}}/)?.[1];
      const mappedValue = attributeId
        ? mappedAttributes.find((plugin) => plugin.attribute.uid === attributeId)
        : undefined;
      const attributeParameters = value.attributeParameters;

      if (attributeId && !mappedValue) {
        errors[key] = {
          errorMessage: `This placeholder uses a plugin that does not exist or is no longer available`,
          errorType: 'error',
        };
      } else if (attributeId && mappedValue?.attribute.parameters?.length && !attributeParameters?.length) {
        errors[key] = {
          errorMessage: `This context plugin requires you to fill in the parameter values. Click the edit button to do so.`,
          errorType: 'error',
        };
      } else if (!selectedProviderTemplatePayload?.placeholderKeys?.includes(key)) {
        errors[key] = {
          errorMessage: `This placeholder is not being used by the Sendgrid template`,
          errorType: 'warning',
        };
      }
    });

    return errors;
  }, [currentTemplatePayloadData, defaultTemplatePayloadData, selectedProviderTemplatePayload, mappedAttributes]);

  const saveDisabled = payloadDataErrors
    ? !!Object.values(payloadDataErrors).find((error) => error.errorType === 'error')
    : false;

  const inStorePayloadData = useMemo<TemplatePayloadData | null>(() => {
    if (!emailActionPayload) return null;
    return deriveTemplatePayloadData(
      (emailActionPayload?.journeyEmailTemplateId === selectedProviderTemplatePayload?.id &&
        selectedProviderTemplatePayload?.placeholderKeys) ||
        [],
      emailActionPayload?.journeyEmailTemplateData ?? {},
      mappedAttributes,
    );
  }, [emailActionPayload, selectedProviderTemplatePayload, mappedAttributes]);

  // Determine if there are unsaved changes using diffObject util
  const hasUnsavedChanges = useMemo(() => {
    if (emailActionPayload?.journeyEmailTemplateId !== selectedProviderTemplatePayload?.id) return true;
    return !isEqual(inStorePayloadData, currentTemplatePayloadData);
  }, [
    inStorePayloadData,
    currentTemplatePayloadData,
    emailActionPayload?.journeyEmailTemplateId,
    selectedProviderTemplatePayload?.id,
  ]);

  const performClose = useCallback(() => {
    emailSidePanel.setEmailContext((prev) => ({ ...prev, isOpen: false }));
    setCurrentTemplatePayloadData(null);
    setSelectedProviderTemplate(null);
  }, [emailSidePanel]);

  const [ConfirmNavigationModal, showConfirmNavigationModal] = useConfirmNavigationModal({
    icon: <IconDiscardChanges />,
    title: 'Cancel changes?',
    description: 'Navigating back will undo current changes made to mapping.',
    primaryAction: { value: 'Remain on page' },
    secondaryAction: { value: 'Discard changes' },
  });

  const onClose = useCallback(async () => {
    // If there are no unsaved changes, close immediately
    if (!hasUnsavedChanges) {
      performClose();
      return;
    }

    // If there ARE unsaved changes, only close if user confirms navigation
    const shouldNavigateAway = await showConfirmNavigationModal();
    if (shouldNavigateAway) {
      performClose();
    }
  }, [hasUnsavedChanges, performClose, showConfirmNavigationModal]);

  const onSave = useCallback(() => {
    // Saving should NOT trigger confirmation modal even if there are unsaved changes.
    if (currentTemplatePayloadData) {
      dispatch(
        updateEmailTemplateData(
          serializeTemplatePayloadData(
            selectedProviderTemplatePayload?.placeholderKeys || [],
            currentTemplatePayloadData || {},
          ),
          selectedProviderTemplate?.key ?? emailActionPayload?.providerTemplateId ?? '',
          emailState?.refId || '',
          selectedLanguageCode,
        ),
      );
    }
    performClose();
  }, [
    dispatch,
    emailActionPayload?.providerTemplateId,
    emailState?.refId,
    performClose,
    selectedLanguageCode,
    selectedProviderTemplate?.key,
    currentTemplatePayloadData,
    selectedProviderTemplatePayload?.placeholderKeys,
  ]);

  const handleSetSelectedProviderTemplate = useCallback(
    (template: TEmailProviderTemplate) => {
      if (template.key === selectedProviderTemplate?.key) {
        return;
      }
      setCurrentTemplatePayloadData(null);
      setSelectedProviderTemplate(template);
    },
    [selectedProviderTemplate?.key],
  );

  const handleConfirmSyncTemplate = useCallback(async () => {
    const result = await refetchEmailTemplate();

    if (result.error) {
      dispatch({
        type: 'SHOW_SNACKBAR',
        payload: { content: 'Unable to sync SendGrid template, please try again.', type: 'error' },
      });
    } else {
      dispatch({
        type: 'SHOW_SNACKBAR',
        payload: { content: 'SendGrid template has been synced.', type: 'success' },
      });
    }
  }, [refetchEmailTemplate, dispatch]);

  const previewData = useMemo<TEmailPreviewData | null>(() => {
    if (!selectedProviderTemplatePayload || !currentTemplatePayloadData) {
      return null;
    }
    const data = Object.entries(currentTemplatePayloadData).reduce((acc, [key, value]) => {
      const mappedValue = mappedAttributes.find(
        (plugin) => plugin.attribute.uid === value.attribute.match(/{{(.*?)}}/)?.[1],
      );
      if (mappedValue) {
        acc[key] = getFormattedContextAttributeDisplay(mappedValue.name, value.attributeParameters);
        return acc;
      }
      acc[key] = value.attribute;
      return acc;
    }, {} as Record<string, string>);

    return {
      html: selectedProviderTemplatePayload.html,
      data,
    };
  }, [selectedProviderTemplatePayload, currentTemplatePayloadData, mappedAttributes]);

  const slidingSidePanelProps = useMemo<TSlidingSidePanelProps>(() => {
    const sidePanelProps: TSlidingSidePanelProps = {
      show: isOpen,
      headerInfo: {
        mainTitle: '',
        goBackIcon: <IconEmail />,
        goBackTitle: 'Setup your email',
        goBackSubTitle: 'Please provide the details for the email you want to send to your target audience',
        goBackActionHandler: () => {
          onClose();
        },
      },
      footerInfo: {
        secondaryActionHandler: () => {
          onClose();
        },
        secondaryActionText: 'Back',
        // Disable footer buttons while syncing template or while template is (re)loading
        secondaryActionDisabled: isEmailTemplateFetching,
      },
      size: 'full',
      isViewOnly,
      viewOnlyHeader: {
        text: 'This is an active experience',
        subText: 'To update the template and its associated data mapping, please deactivate the experience first.',
      },
    };

    if (!isViewOnly) {
      sidePanelProps.footerInfo = {
        ...sidePanelProps.footerInfo,
        primaryActionHandler: () => {
          onSave();
        },
        primaryActionText: 'Apply',
        primaryActionDisabled: isEmailTemplateFetching || saveDisabled,
        primaryActionLoading: isEmailTemplateFetching,
      };
    }

    return sidePanelProps;
  }, [onClose, onSave, isOpen, isViewOnly, isEmailTemplateFetching, saveDisabled]);

  useEffect(() => {
    if (selectedProviderTemplatePayload) {
      setCurrentTemplatePayloadData(
        deriveTemplatePayloadData(
          selectedProviderTemplatePayload?.placeholderKeys ?? [],
          emailActionPayload?.journeyEmailTemplateId === selectedProviderTemplatePayload?.id
            ? emailActionPayload?.journeyEmailTemplateData ?? {}
            : {},
          mappedAttributes,
        ),
      );
    }
  }, [selectedProviderTemplatePayload, emailActionPayload, mappedAttributes]);

  useEffect(() => {
    if (providerTemplateOptions.length > 0 && isOpen)
      setSelectedProviderTemplate(
        providerTemplateOptions.find((t) => t.key === emailActionPayload?.journeyEmailTemplateId) || null,
      );
  }, [emailActionPayload?.journeyEmailTemplateId, providerTemplateOptions, isOpen]);

  return (
    <div className={MAIN_CLASS} key={`email-side-panel-${emailState?.refId || ''}-${selectedLanguageCode}`}>
      <SlidingSidePanel {...slidingSidePanelProps}>
        {emailTemplatesError || !emailTemplates || emailTemplates.length === 0 ? (
          <EmailSMSUnavailable type="email" />
        ) : (
          <div
            className={`${CLASSES.CONTAINER} ${isViewOnly ? `${CLASSES.CONTAINER}--view-only` : ''}`}
            {...{ inert: isViewOnly ? '' : undefined }}
          >
            <div className={CLASSES.TEMPLATE_AREA}>
              <TemplateSelector
                type="email"
                onTemplateSelect={handleSetSelectedProviderTemplate}
                templates={providerTemplateOptions}
                selectedTemplate={selectedProviderTemplate}
                isTemplateError={!!emailTemplateError}
              />
              <AttributesMappingTable
                type="email"
                defaultTemplatePayloadData={defaultTemplatePayloadData}
                providerTemplatePayloadData={currentTemplatePayloadData}
                payloadDataErrors={payloadDataErrors}
                isLoading={isEmailTemplateFetching}
                onChange={setCurrentTemplatePayloadData}
                onConfirmSyncTemplate={handleConfirmSyncTemplate}
                mappedAttributes={mappedAttributes}
                isTemplateError={!!emailTemplateError}
              />
            </div>
            <div className={CLASSES.PREVIEW_AREA}>
              <EmailTemplatePreview
                previewData={previewData}
                isLoading={isEmailTemplateFetching}
                isTemplateError={!!emailTemplateError}
              />
            </div>
          </div>
        )}
        {ConfirmNavigationModal()}
      </SlidingSidePanel>
    </div>
  );
}

export default EmailSidePanel;
